import update from "immutability-helper";
import React from "react";
import injectSheet from "react-jss";
import shortid from "shortid";
import {
	FileInput,
	FormInputContainer,
	IconButton,
	SelectInput,
	TextButton,
} from "../../";
import { Add, Cancel, Invisible, Visible } from "../../icons/actionIcons";

class AdminNodeDetail extends React.Component {
	static getDerivedStateFromProps(nextProps, prevState) {
		if (nextProps.node && nextProps.node.id !== prevState.newNode.id) {
			const { fields } = nextProps.config[nextProps.node.nodeType];
			const copy = {};
			copy.id = nextProps.node.id;
			fields.forEach((field) => {
				copy[field] = nextProps.node[field];
			});
			return {
				hasChanges: false,
				newFile: null,
				newFiles: [null, null, null],
				newNode: copy,
			};
		}
		return null;
	}

	constructor(props) {
		super(props);

		this.state = {
			hasChanges: false,
			newNode: {},
			newFile: null,
			newFiles: [null, null, null],
			generatedIds: [...Array(3).keys()].map(() => shortid.generate()),
		};

		this.handleVisibility = this.handleVisibility.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.uploadImage = this.uploadImage.bind(this);
		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleElementInputChange = this.handleElementInputChange.bind(this);
		this.handleSelect = this.handleSelect.bind(this);
		this.handleNewFile = this.handleNewFile.bind(this);
		this.handleNewFiles = this.handleNewFiles.bind(this);
		this.addElement = this.addElement.bind(this);
		this.removeElement = this.removeElement.bind(this);
	}

	handleVisibility() {
		const { node } = this.props;
		const { newNode } = this.state;

		const updatedNode = update(newNode, {
			isVisible: { $set: !newNode.isVisible },
		});

		this.setState({
			newNode: updatedNode,
		});

		this.props[`update${node.nodeType}Mutation`]({
			variables: this.props.config[node.nodeType].variables(node.id, {
				isVisible: !newNode.isVisible,
			}),
		});
	}

	async handleSubmit() {
		const { node } = this.props;
		const { newFile, newFiles } = this.state;

		let { newNode } = this.state;

		newNode.title =
			newNode.title && newNode.title !== "" ? newNode.title : "neues Element";

		if ("elements" in newNode) {
			newNode = {
				...newNode,
				elements: newNode.elements.map((element) => ({
					...element,
					title:
						element.title && element.title !== ""
							? element.title
							: "neues Element",
				})),
			};
		}

		if ("minSelect" in newNode || "maxSelect" in newNode) {
			const min = Number.parseInt(
				!newNode.minSelect ? 1 : newNode.minSelect,
				10,
			);
			let max = Number.parseInt(!newNode.maxSelect ? 1 : newNode.maxSelect, 10);
			if (min > max) {
				max = min;
			}
			newNode = update(newNode, {
				maxSelect: { $set: max },
				minSelect: { $set: min },
			});
		}

		if (newFile !== null) {
			const path = await this.uploadImage(newFile);
			newNode = update(newNode, {
				images: { $set: [path] },
			});
		}

		if (newFiles.filter((el) => el !== null).length > 0) {
			const paths = await Promise.all(
				newFiles.map(async (file) =>
					file !== null ? this.uploadImage(file) : null,
				),
			);

			paths.forEach((path, index) => {
				if (path !== null) {
					newNode = update(newNode, {
						elements: { [index]: { images: { $set: [path] } } },
					});
				}
			});
		}

		this.props[`update${node.nodeType}Mutation`]({
			variables: this.props.config[node.nodeType].variables(node.id, newNode),
		});

		return this.setState({
			hasChanges: false,
			newFile: null,
			newFiles: [null, null, null],
			newNode,
		});
	}

	async uploadImage(file) {
		return this.props
			.uploadImageMutation({
				variables: { file },
			})
			.then((res) => res.data.uploadImage.path)
			.catch((error) => {
				console.log(error);
				return false;
			});
	}

	handleInputChange(event) {
		const { target } = event;
		const value = target.type === "checkbox" ? target.checked : target.value;
		const { name } = target;

		const { newNode } = this.state;

		const updatedNode = update(newNode, {
			[name]: { $set: value },
		});

		return this.setState({
			hasChanges: true,
			newNode: updatedNode,
		});
	}

	handleElementInputChange(event, index) {
		const { target } = event;
		const value = target.type === "checkbox" ? target.checked : target.value;
		const { name } = target;

		const { newNode } = this.state;

		const updatedNode = update(newNode, {
			elements: { [index]: { [name.slice(0, -1)]: { $set: value } } },
		});

		this.setState({
			hasChanges: true,
			newNode: updatedNode,
		});
	}

	handleSelect(field, id) {
		const { newNode } = this.state;
		const index =
			newNode[field] &&
			Array.isArray(newNode[field]) &&
			newNode[field].findIndex((el) => el === id);
		let updatedNode;

		if (index < 0) {
			updatedNode = update(newNode, {
				[field]: { $push: [id] },
			});
		} else {
			updatedNode = update(newNode, {
				[field]: { $splice: [[index, 1]] },
			});
		}

		this.setState({
			hasChanges: true,
			newNode: updatedNode,
		});
	}

	handleNewFile(file) {
		this.setState({
			hasChanges: true,
			newFile: file,
		});
	}

	handleNewFiles(index, file) {
		const { newFiles } = this.state;

		const updatedFiles = update(newFiles, {
			[index]: { $set: file },
		});

		this.setState({
			hasChanges: true,
			newFiles: updatedFiles,
		});
	}

	addElement() {
		const { newNode } = this.state;

		const updatedNode = update(newNode, {
			elements: (elements) => update(elements || [], { $push: [{}] }),
		});

		this.setState({
			hasChanges: true,
			newNode: updatedNode,
		});
	}

	removeElement(index) {
		const updatedState = update(this.state, {
			hasChanges: { $set: true },
			newNode: { elements: { $splice: [[index, 1]] } },
			newFiles: { $splice: [[index, 1]], $push: [null] },
		});

		this.setState(updatedState);
	}

	render() {
		const { classes, config, deleteNodeMutation, options, setActive } =
			this.props;

		const { hasChanges, newFile, newFiles, newNode, generatedIds } = this.state;

		const { node } = this.props;

		const fields = node && node.nodeType ? config[node.nodeType].fields : [];

		return (
			<div className={`${classes.container}`}>
				{node &&
				newNode &&
				!(Object.keys(newNode).length === 0) &&
				newNode.constructor === Object ? (
					<React.Fragment>
						<div className={`${classes.topDetailContainer}`}>
							<h2>{node.title}</h2>
							<div className={`${classes.topActionContainer}`}>
								{node.nodeType !== "Root" && (
									<React.Fragment>
										<IconButton
											handleClick={this.handleVisibility}
											icon={newNode.isVisible ? <Visible /> : <Invisible />}
											index={0}
											large
											primary={!newNode.isVisible}
											tip={newNode.isVisible ? "verstecken" : "veröffentlichen"}
										/>
										<IconButton
											handleClick={() => {
												deleteNodeMutation({
													variables: { id: node.id },
												});
												setActive(node.parent);
											}}
											icon={<Cancel />}
											index={0}
											large
											tip="löschen"
											modal
											modalText="Wollen Sie dieses Element unwiderruflich löschen?"
											modalTitle="Element löschen?"
										/>
									</React.Fragment>
								)}
							</div>
						</div>
						<div className={`${classes.inputContainer}`}>
							{fields.includes("title") && (
								<FormInputContainer
									filled={newNode.title}
									forHtml="title"
									label="Titel"
									width={100}
								>
									<input
										id="title"
										name="title"
										onChange={this.handleInputChange}
										type="text"
										value={newNode.title}
									/>
								</FormInputContainer>
							)}
							{fields.includes("description") && (
								<FormInputContainer
									filled={newNode.description}
									forHtml="description"
									label="Beschreibung"
									width={100}
								>
									<textarea
										id="description"
										name="description"
										onChange={this.handleInputChange}
										value={newNode.description || ""}
									/>
								</FormInputContainer>
							)}
							{fields.includes("minSelect") && (
								<FormInputContainer
									filled={newNode.minSelect}
									forHtml="minSelect"
									label="Minimale Auswahl"
									width={50}
								>
									<input
										id="minSelect"
										name="minSelect"
										onChange={this.handleInputChange}
										type="number"
										value={newNode.minSelect}
									/>
								</FormInputContainer>
							)}
							{fields.includes("maxSelect") && (
								<FormInputContainer
									filled={newNode.maxSelect}
									forHtml="maxSelect"
									label="Maximale Auswahl"
									width={50}
								>
									<input
										id="maxSelect"
										name="maxSelect"
										onChange={this.handleInputChange}
										type="number"
										value={newNode.maxSelect}
									/>
								</FormInputContainer>
							)}
							{fields.includes("images") && (
								<FormInputContainer
									filled
									forHtml="images"
									label="Bild"
									width={100}
								>
									<FileInput
										accept="image/*"
										file={
											newFile === null && Array.isArray(newNode.images)
												? newNode.images[0]
												: newFile
										}
										maxSize={4 * 1024 * 1024}
										handleSelect={this.handleNewFile}
									/>
								</FormInputContainer>
							)}
							{fields.includes("elements") &&
								Array.isArray(newNode.elements) && (
									<div className={`${classes.elementsContainer}`}>
										<div className={`${classes.elementsContainerTop}`}>
											<div className={`${classes.elementsContainerTopLabel}`}>
												Elemente
											</div>
											<div className={`${classes.elementsContainerTopAction}`}>
												{newNode.elements.length < 3 && (
													<IconButton
														icon={<Add />}
														handleClick={this.addElement}
														tip="hinzufügen"
													/>
												)}
											</div>
										</div>
										<div className={`${classes.elementsContainerElements}`}>
											{newNode.elements.map((element, index) => (
												<div
													className={`${classes.element}`}
													key={`element-${generatedIds[index]}`}
												>
													<div className={`${classes.elementDelete}`}>
														<IconButton
															icon={<Cancel />}
															handleClick={() => this.removeElement(index)}
															tip="löschen"
														/>
													</div>
													<FormInputContainer
														filled={element.title}
														forHtml={`title${index}`}
														label="Titel"
													>
														<input
															id={`title${index}`}
															name={`title${index}`}
															onChange={(event) =>
																this.handleElementInputChange(event, index)
															}
															type="text"
															value={element.title ? element.title : ""}
														/>
													</FormInputContainer>
													<FormInputContainer
														filled={element.description}
														forHtml={`description${index}`}
														label="Beschreibung"
													>
														<textarea
															id={`description${index}`}
															name={`description${index}`}
															onChange={(event) =>
																this.handleElementInputChange(event, index)
															}
															type="text"
															value={
																element.description ? element.description : ""
															}
														/>
													</FormInputContainer>
													<FormInputContainer
														filled
														forHtml="images"
														label="Bild"
														width={100}
													>
														<FileInput
															accept="image/*"
															file={
																newFiles[index] === null &&
																Array.isArray(element.images)
																	? element.images[0]
																	: newFiles[index]
															}
															maxSize={4 * 1024 * 1024}
															handleSelect={(file) =>
																this.handleNewFiles(index, file)
															}
														/>
													</FormInputContainer>
												</div>
											))}
										</div>
									</div>
								)}
							{fields.includes("conflicts") && (
								<FormInputContainer label="Konflikt" width={100}>
									<SelectInput
										field="conflicts"
										handleSelect={(field, id) => this.handleSelect(field, id)}
										options={options}
										placeholder="Hat mit keinem Grundriss einen Konflikt"
										selection={newNode.conflicts}
									/>
								</FormInputContainer>
							)}
							{fields.includes("requires") && (
								<FormInputContainer label="Benötigt" width={100}>
									<SelectInput
										field="requires"
										handleSelect={(field, id) => this.handleSelect(field, id)}
										options={options}
										placeholder="Benötigt keinen speziellen Grundriss"
										selection={newNode.requires}
									/>
								</FormInputContainer>
							)}
							{fields.includes("area") && (
								<FormInputContainer
									filled={newNode.area !== ""}
									forHtml="area"
									label="Fläche"
									width={100}
								>
									<input
										id="area"
										name="area"
										onChange={this.handleInputChange}
										type="number"
										value={newNode.area}
									/>
								</FormInputContainer>
							)}
							{fields.includes("pricePerArea") && (
								<FormInputContainer
									filled
									forHtml="pricePerArea"
									label="Preis pro m2"
									width={100}
								>
									<input
										id="pricePerArea"
										name="pricePerArea"
										onChange={this.handleInputChange}
										type="checkbox"
										checked={newNode.pricePerArea}
									/>
								</FormInputContainer>
							)}
							{fields.includes("price") && (
								<FormInputContainer
									filled={newNode.price !== ""}
									forHtml="price"
									label="Preis"
									width={100}
								>
									<input
										id="price"
										name="price"
										onChange={this.handleInputChange}
										type="number"
										value={newNode.price}
									/>
								</FormInputContainer>
							)}
						</div>
						<div className={`${classes.inputContainer} submit`}>
							<TextButton
								disabled={!hasChanges}
								text="speichern"
								handleClick={this.handleSubmit}
								primary
							/>
						</div>
					</React.Fragment>
				) : (
					<p>
						Wählen Sie in der Navigation (links) ein Element aus und klicken Sie
						auf das &ldquo;bearbeiten&rdquo; Icon
					</p>
				)}
			</div>
		);
	}
}

const styles = (theme) => ({
	container: {
		margin: `${theme.sizes.gutter / 2 + 2}px ${theme.sizes.gutter / 2}px`,
		padding: `${theme.sizes.gutter}px ${theme.sizes.gutter / 2}px`,
		backgroundColor: theme.colors.main_2,
		color: theme.colors.secondary_3b,
		display: "flex",
		justifyContent: "center",
		flexWrap: "wrap",
		"& > p": {
			color: theme.colors.main_3,
			margin: 0,
			padding: `${theme.sizes.gutter}px`,
		},
	},
	topDetailContainer: {
		width: "100%",
		display: "flex",
		justifyContent: "space-between",
		"& > h2": {
			width: "100%",
			margin: 0,
		},
	},
	topActionContainer: {
		display: "flex",
		"& > div": {
			margin: "0 2px",
		},
	},
	formContainer: {
		width: "100%",
		height: "100%",
	},
	inputContainer: {
		width: "100%",
		display: "flex",
		flexWrap: "wrap",
		paddingTop: `${theme.sizes.gutter}px`,
		"&.submit": {
			justifyContent: "flex-end",
		},
	},
	elementsContainer: {
		width: "100%",
	},
	elementsContainerTop: {
		width: "100%",
		display: "flex",
		alignItems: "center",
		paddingTop: `${theme.sizes.gutter}px`,
	},
	elementsContainerTopLabel: {
		fontSize: "80%",
	},
	elementsContainerTopAction: {
		marginLeft: "10px",
	},
	elementsContainerElements: {
		width: "100%",
	},
	element: {
		width: "100%",
		position: "relative",
		margin: `${theme.sizes.gutter / 2}px 0`,
		border: `1px solid ${theme.colors.secondary_3a}`,
		borderRadius: "3px",
	},
	elementDelete: {
		position: "absolute",
		top: 0,
		right: 0,
		transform: "translateY(-50%)",
	},
});

export default injectSheet(styles)(AdminNodeDetail);



// WEBPACK FOOTER //
// ./src/modules/admin/components/adminNodeDetail.jsx